import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useResponsive, useRoleNavigation, useUnsavedChangesWarning } from '../../hooks';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';
import { queryClient, queryKeys } from '../../providers/QueryProvider';
import { extractKpiErrorMessage, validateKpiRequiredFields, validateKpiTargetConfigs } from '../../utils/kpiErrors';
import { normalizeArabicNumbers } from '../../utils/rtl';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Breadcrumb, KPIDeleteConfirmModal, MultiSelectDropdown } from '../../components';
import CancelConfirmModal from '../../components/CancelConfirmModal';
import { useCancelConfirm } from '../../hooks/useCancelConfirm';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { initiativesApi, goalsApi, kpisApi, organizationalUnitsApi } from '../../services/apiService';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { unitTreeOptions } from '../../utils/unitTree';
import { StrategicGoalList, KPIType, Frequency, Polarity, OrganizationalUnitList } from '../../types';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
import KpiAnnualTargetSection, { KpiTargetConfig, emptyKpiTargetConfig, buildKpiTargetPayload, configFromKpi } from '../kpis/components/KpiAnnualTargetSection';
type RouteProps = RouteProp<MainStackParamList, 'AddInitiative'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// KPI Form Interface
// ============================================================

interface KPIFormData {
  id: string;
  name: string;
  weight: string;
  description: string;
  kpiType: KPIType | '';
  polarity: Polarity | '';
  startDate: string;
  unit: string;
  frequency: Frequency | '';
  formula: string;
  targetValue: string;
  dataSource: string;
  target: KpiTargetConfig;
  isExpanded: boolean;
  responsibleUnits: string[];
}

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronDownIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronUpIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 15L12 9L6 15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrashIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const DragHandleIcon = ({ size = 20, color = tokens.colors.raw.gray["400"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M8 6H8.01M8 12H8.01M8 18H8.01M16 6H16.01M16 12H16.01M16 18H16.01" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const MenuIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M4 6H20M4 12H20M4 18H20" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const CalendarIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Dropdown Component
// ============================================================

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  value: string;
  options: DropdownOption[];
  placeholder: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const Dropdown = ({ value, options, placeholder, onChange, disabled }: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonLayout, setButtonLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const buttonRef = useRef<View>(null);
  const { colors, isDark } = useTheme();
  const selectedOption = options.find(opt => opt.value === value);

  const dropdownTheme = {
    bg: colors.card,
    border: colors.border,
    text: colors.textPrimary,
    placeholder: isDark ? tokens.colors.raw.gray["500"] : tokens.colors.raw.gray["400"],
    icon: colors.textMuted,
    disabledBg: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["100"],
    menuBg: colors.card,
    menuBorder: colors.border,
    itemBorder: isDark ? colors.border : tokens.colors.raw.slate["100"],
  };

  const handleOpen = () => {
    if (disabled) return;
    buttonRef.current?.measureInWindow((x: number, y: number, width: number, height: number) => {
      setButtonLayout({ x, y, width, height });
      setIsOpen(true);
    });
  };

  const menuDomRef = useRef<HTMLDivElement | null>(null);
  const menuCallbackRef = useCallback((node: any) => {
    menuDomRef.current = node as HTMLDivElement | null;
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const menuEl = menuDomRef.current;
      if (menuEl && menuEl.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    const handleScroll = (e: Event) => {
      const menuEl = menuDomRef.current;
      if (menuEl && menuEl.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const portalContent = isOpen ? ReactDOM.createPortal(
    <View
      ref={menuCallbackRef}
      style={{
        position: 'fixed' as any,
        top: buttonLayout.y + buttonLayout.height + 4,
        start: buttonLayout.x,
        width: buttonLayout.width,
        backgroundColor: dropdownTheme.menuBg,
        borderWidth: tokens.spacing.absolute["px"],
        borderColor: dropdownTheme.menuBorder,
        borderRadius: tokens.radii.absolute["radius-12"],
        maxHeight: 250,
        zIndex: 99999,
        elevation: 99999,
        overflow: 'hidden' as any,
      }}
      // @ts-ignore - web style
      onMouseDown={(e: any) => e.stopPropagation()}
    >
      <ScrollView style={{ maxHeight: 240 }} nestedScrollEnabled>
        <View style={{ boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)' } as any}>
          {options.map((option) => (
            <TouchableOpacity
              key={option.value}
              style={[styles.dropdownItem, { borderBottomColor: dropdownTheme.itemBorder }]}
              onPress={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              <Text style={[styles.dropdownItemText, { color: dropdownTheme.text }]}>{option.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>
    </View>,
    document.body
  ) : null;

  return (
    <View style={styles.dropdownContainer}>
      <TouchableOpacity
        ref={buttonRef as any}
        style={[
          styles.dropdownButton,
          { backgroundColor: dropdownTheme.bg, borderColor: dropdownTheme.border },
          disabled && { backgroundColor: dropdownTheme.disabledBg }
        ]}
        onPress={handleOpen}
        disabled={disabled}
      >
        <ChevronDownIcon size={16} color={dropdownTheme.icon} />
        <Text style={[
          styles.dropdownButtonText,
          { color: dropdownTheme.text },
          !selectedOption && { color: dropdownTheme.placeholder }
        ]}>
          {selectedOption?.label || placeholder}
        </Text>
      </TouchableOpacity>
      {portalContent}
    </View>
  );
};

// ============================================================
// KPI Options
// ============================================================

// Note: These will be translated at render time using the t function
const getKpiTypeOptions = (t: (key: string) => string): DropdownOption[] => [
  { value: 'strategic', label: t('kpis.strategic') },
  { value: 'operational', label: t('kpis.operational') },
  { value: 'efficiency', label: t('kpis.efficiency') },
  { value: 'effectiveness', label: t('kpis.effectiveness') },
  { value: 'impact', label: t('kpis.impact') },
];

const getPolarityOptions = (t: (key: string) => string): DropdownOption[] => [
  { value: 'positive', label: t('kpis.ascending') },
  { value: 'negative', label: t('kpis.descending') },
];

const getUnitOptions = (t: (key: string) => string): DropdownOption[] => [
  { value: '%', label: t('kpis.percentage') },
  { value: 'number', label: t('kpis.number') },
  { value: 'currency', label: t('kpis.currency') },
];

const getFrequencyOptions = (t: (key: string) => string): DropdownOption[] => [
  { value: 'daily', label: t('kpis.daily') },
  { value: 'weekly', label: t('kpis.weekly') },
  { value: 'monthly', label: t('kpis.monthly') },
  { value: 'quarterly', label: t('kpis.quarterly') },
  { value: 'half_yearly', label: t('kpis.semiAnnual') },
  { value: 'yearly', label: t('kpis.annual') },
];


// ============================================================
// Main Component
// ============================================================

export default function AddInitiativeScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { goalId, initiativeId, scrollToKpi } = route.params || {};
  const { showSuccess, showError } = useNotification();
  const { navigateToHome, isViewOnly } = useRoleNavigation();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const styles = useMemo(() => getStyles(isRTL), [isRTL]);
  const { translate, translateOrg } = useAutoTranslate();
  const { isMobile, isTablet, isDesktop, isSmallMobile, containerPadding, cardPadding, sectionSpacing } = useResponsive();

  useEffect(() => {
    if (isViewOnly) {
      if (navigation.canGoBack()) navigation.goBack();
      else navigateToHome();
    }
  }, [isViewOnly, navigation, navigateToHome]);

  // Theme colors for form elements
  const themeColors = {
    mainCard: colors.card,
    mainCardBorder: colors.cardBorder,
    pageTitle: colors.textPrimary,
    section: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["50"],
    sectionTitle: colors.textPrimary,
    label: colors.textPrimary,
    input: colors.card,
    inputBorder: colors.border,
    inputText: colors.textPrimary,
    placeholder: isDark ? tokens.colors.raw.gray["500"] : tokens.colors.raw.gray["400"],
    iconColor: colors.textMuted,
    dropdown: colors.card,
    dropdownBorder: colors.border,
    dropdownText: colors.textPrimary,
    kpiCard: colors.card,
    kpiCardBorder: colors.cardBorder,
    kpiCardHeader: colors.card,
    kpiNumber: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["100"],
  };

  // Refs for scrolling to KPI section
  const scrollViewRef = useRef<ScrollView>(null);
  const kpiSectionY = useRef<number>(0);

  // Edit mode flag
  const isEditMode = !!initiativeId;

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    strategicGoal: goalId ? goalId.toString() : '',
    weight: '',
    responsiblePerson: '',
    organizationalUnits: [] as string[],
    resources: '',
    startDate: '',
    endDate: '',
    status: 'planning' as 'planning' | 'in_progress' | 'completed' | 'on_hold' | 'cancelled',
  });

  // KPIs state
  const [kpis, setKpis] = useState<KPIFormData[]>([]);
  const [kpiSectionExpanded, setKpiSectionExpanded] = useState(true);
  const [deletedKpiIds, setDeletedKpiIds] = useState<number[]>([]);
  const [kpiPendingDelete, setKpiPendingDelete] = useState<{ id: string; backendId: number | null; name: string } | null>(null);

  const [loading, setLoading] = useState(false);
  const [goalsLoading, setGoalsLoading] = useState(true);
  const [unitsLoading, setUnitsLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [goals, setGoals] = useState<StrategicGoalList[]>([]);
  const [organizationalUnits, setOrganizationalUnits] = useState<OrganizationalUnitList[]>([]);

  useUnsavedChangesWarning(!!formData.title || !!formData.description);

  // Fetch goals for dropdown
  useEffect(() => {
    const fetchGoals = async () => {
      try {
        const response = await goalsApi.list();
        setGoals(response.results || []);
      } catch (error) {
        console.error('Error fetching goals:', error);
      } finally {
        setGoalsLoading(false);
      }
    };
    fetchGoals();
  }, []);

  // Fetch organizational units for dropdown
  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const response = await organizationalUnitsApi.list({ page_size: 9999 });
        setOrganizationalUnits(response.results || []);
      } catch (error) {
        console.error('Error fetching organizational units:', error);
      } finally {
        setUnitsLoading(false);
      }
    };
    fetchUnits();
  }, []);

  // Fetch initiative data when in edit mode
  useEffect(() => {
    const fetchInitiative = async () => {
      if (!initiativeId) return;

      try {
        setInitialLoading(true);
        const initiative = await initiativesApi.get(initiativeId);

        // Populate form with initiative data
        setFormData({
          title: initiative.title || '',
          description: initiative.description || '',
          strategicGoal: initiative.strategic_goal?.toString() || '',
          weight: initiative.weight?.toString() || '',
          responsiblePerson: initiative.responsible_person || '',
          organizationalUnits: (initiative.responsible_units || []).map((id: number) => id.toString()),
          resources: initiative.resources_description || '',
          startDate: initiative.start_date || '',
          endDate: initiative.end_date || '',
          status: initiative.status || 'planning',
        });

        // Fetch KPIs for this initiative
        try {
          const kpisResponse = await kpisApi.list({ initiative: initiativeId, page_size: 100 });
          const existingKpis: KPIFormData[] = (kpisResponse.results || []).map((kpi: any) => ({
            id: kpi.id.toString(),
            name: kpi.name || '',
            weight: kpi.weight?.toString() || '0',
            description: kpi.description || '',
            kpiType: kpi.kpi_type || '',
            polarity: kpi.polarity || '',
            startDate: kpi.measurement_start_date || '',
            unit: kpi.unit || '',
            frequency: kpi.frequency || '',
            formula: kpi.measurement_formula || '',
            targetValue: kpi.target_value?.toString() || '0',
            dataSource: kpi.data_source || '',
            target: configFromKpi(kpi),
            isExpanded: false,
            responsibleUnits: (kpi.responsible_units || []).map((id: number) => String(id)),
          }));
          setKpis(existingKpis);
        } catch (error) {
          console.error('Error fetching KPIs:', error);
        }
      } catch (error) {
        console.error('Error fetching initiative:', error);
        showError(t('messages.error.loadFailed'));
      } finally {
        setInitialLoading(false);
      }
    };

    fetchInitiative();
  }, [initiativeId]);

  // Scroll to KPI section function
  const scrollToKpiSection = useCallback(() => {
    if (scrollToKpi && kpiSectionY.current > 0) {
      setTimeout(() => {
        scrollViewRef.current?.scrollTo({ y: kpiSectionY.current - 100, animated: true });
      }, 100);
    }
  }, [scrollToKpi]);

  // Scroll to KPI section when data is loaded
  useEffect(() => {
    if (!initialLoading && scrollToKpi) {
      scrollToKpiSection();
    }
  }, [initialLoading, scrollToKpi, scrollToKpiSection]);

  const goalOptions: DropdownOption[] = goals.map(goal => ({
    value: goal.id.toString(),
    label: translate(goal.title),
  }));

  // Every department and section, each followed by its sub-units.
  const orgUnitOptions = unitTreeOptions(organizationalUnits, unit => translateOrg(unit.name));

  // Create empty KPI form
  const createEmptyKPI = (): KPIFormData => ({
    id: Date.now().toString(),
    name: '',
    weight: '30',
    description: '',
    kpiType: '',
    polarity: '',
    startDate: '',
    unit: '%',
    frequency: 'yearly',
    formula: '',
    targetValue: '0.0',
    dataSource: '',
    target: emptyKpiTargetConfig,
    isExpanded: true,
    responsibleUnits: [],
  });

  // Add new KPI
  const handleAddKPI = () => {
    setKpis([...kpis, createEmptyKPI()]);
  };

  // Remove KPI — open confirmation first
  const handleRemoveKPI = (kpiId: string) => {
    const target = kpis.find(k => k.id === kpiId);
    const numericId = parseInt(kpiId, 10);
    const backendId = !isNaN(numericId) && numericId < 1000000 ? numericId : null;
    setKpiPendingDelete({
      id: kpiId,
      backendId,
      name: target?.name || '',
    });
  };

  const confirmRemoveKPI = () => {
    if (!kpiPendingDelete) return;
    const { id, backendId } = kpiPendingDelete;
    if (backendId !== null) {
      setDeletedKpiIds(prev => [...prev, backendId]);
    }
    setKpis(prev => prev.filter(kpi => kpi.id !== id));
    setKpiPendingDelete(null);
  };

  // Toggle KPI expand/collapse
  const handleToggleKPI = (kpiId: string) => {
    setKpis(kpis.map(kpi =>
      kpi.id === kpiId ? { ...kpi, isExpanded: !kpi.isExpanded } : kpi
    ));
  };

  // Update KPI field
  const updateKPIField = (kpiId: string, field: string, value: string) => {
    setKpis(kpis.map(kpi =>
      kpi.id === kpiId ? { ...kpi, [field]: value } : kpi
    ));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = t('validation.required', { field: t('initiatives.initiativeName') });
    }
    if (!formData.description.trim()) {
      newErrors.description = t('validation.required', { field: t('initiatives.initiativeDescription') });
    }
    if (!formData.strategicGoal) {
      newErrors.strategicGoal = t('validation.strategicGoalRequired');
    }
    if (formData.organizationalUnits.length === 0) {
      newErrors.organizationalUnits = t('validation.organizationalUnitRequired');
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return false;

    // Validate KPI required fields
    const kpiError = validateKpiRequiredFields(kpis, t);
    if (kpiError) {
      showError(kpiError);
      return false;
    }
    // Validate KPI annual-target config (end date, pattern, step values) — mirrors
    // the backend KPISerializer.validate() rules so an invalid setup (e.g. a
    // missing verification/measurement-end date) is caught here instead of
    // round-tripping to a 400 that gets masked by the success toast below.
    const kpiTargetError = validateKpiTargetConfigs(kpis, t);
    if (kpiTargetError) {
      showError(kpiTargetError);
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showError(t('validation.fillRequired'));
      return;
    }

    setLoading(true);
    try {
      // Prepare initiative data
      const initiativeData: Record<string, any> = {
        title: formData.title,
        description: formData.description,
        strategic_goal: parseInt(formData.strategicGoal),
        responsible_units: formData.organizationalUnits.map((id: string) => parseInt(id)),
        resources_description: formData.resources,
        start_date: formData.startDate || null,
        end_date: formData.endDate || null,
        status: formData.status,
      };

      let savedInitiative;

      if (isEditMode && initiativeId) {
        // Update existing initiative
        savedInitiative = await initiativesApi.update(initiativeId, initiativeData);
      } else {
        // Create new initiative
        savedInitiative = await initiativesApi.create(initiativeData);
      }

      // Delete removed KPIs from backend
      let kpiDeleteFailed = 0;
      if (deletedKpiIds.length > 0) {
        for (const kpiId of deletedKpiIds) {
          try {
            await kpisApi.delete(kpiId);
          } catch (deleteError: any) {
            kpiDeleteFailed++;
          }
        }
      }
      if (kpiDeleteFailed > 0) {
        showError(t('kpis.deleteFailed'));
      }

      // Handle KPIs - create new ones and update existing ones
      const validKpis = kpis.filter(kpi => kpi.name.trim());
      const kpiErrors: string[] = [];
      // Maps a new KPI's temporary timestamp id → the real backend id once it
      // saves. When some KPIs fail we keep the user on the form to retry; without
      // this remap the already-saved KPIs would still carry timestamp ids and get
      // re-created (duplicate-name 400) on the next save.
      const savedIdRemap: Record<string, number> = {};
      if (validKpis.length > 0) {
        const savedId = savedInitiative?.id || initiativeId;
        for (const kpi of validKpis) {
          const kpiData = {
            name: kpi.name,
            description: kpi.description,
            initiative: savedId,
            kpi_type: (kpi.kpiType || 'operational') as KPIType,
            polarity: (kpi.polarity || 'positive') as Polarity,
            unit: kpi.unit || '%',
            frequency: (kpi.frequency || 'monthly') as Frequency,
            target_value: (parseFloat(kpi.targetValue) || 0).toFixed(2),
            weight: kpi.weight || '0',
            measurement_start_date: kpi.startDate || null,
            measurement_formula: kpi.formula || '',
            data_source: kpi.dataSource || '',
            source_type: 'initiative',
            responsible_units: kpi.responsibleUnits.map(Number),
          };

          // Check if this is an existing KPI (small numeric ID) or new one (timestamp ID)
          const numericId = parseInt(kpi.id, 10);
          if (!isNaN(numericId) && numericId < 1000000) {
            try {
              await kpisApi.update(numericId, {
                ...kpiData,
                ...buildKpiTargetPayload(kpi.target, {
                  unit: kpi.unit,
                  frequency: kpi.frequency,
                  startDate: kpi.startDate,
                }),
              });
            } catch (updateError: any) {
              const errMsg = extractKpiErrorMessage(updateError?.response?.data);
              kpiErrors.push(t('kpis.kpiUpdateError', { name: kpi.name, error: errMsg }));
              console.warn(`KPI ${kpi.id} update failed:`, updateError?.response?.data);
            }
          } else {
            try {
              const created = await kpisApi.create({
                ...kpiData,
                ...buildKpiTargetPayload(kpi.target, {
                  unit: kpi.unit,
                  frequency: kpi.frequency,
                  startDate: kpi.startDate,
                }),
              });
              if (created?.id != null) savedIdRemap[kpi.id] = created.id;
            } catch (createError: any) {
              const errMsg = extractKpiErrorMessage(createError?.response?.data);
              kpiErrors.push(t('kpis.kpiSaveError', { name: kpi.name, error: errMsg }));
              console.warn(`KPI create failed:`, createError?.response?.data);
            }
          }
        }
      }

      queryClient.invalidateQueries({ queryKey: queryKeys.initiatives.all });

      // Do NOT claim success when one or more KPIs failed to save — the earlier
      // behaviour showed both an error and a success toast, and the success one
      // masked the failure, so the initiative "looked saved" while its new KPIs
      // silently never persisted. Surface the real errors instead and keep the
      // user on the form so they can fix and retry.
      if (kpiErrors.length > 0) {
        // Promote the KPIs that DID save to their real backend ids so retrying
        // updates them instead of re-creating (which would 400 on duplicate name).
        if (Object.keys(savedIdRemap).length > 0) {
          setKpis(prev => prev.map(k =>
            savedIdRemap[k.id] != null ? { ...k, id: String(savedIdRemap[k.id]) } : k
          ));
        }
        showError(kpiErrors.join('\n'));
        return;
      }

      showSuccess(isEditMode ? t('initiatives.updateSuccess') : t('initiatives.createSuccess'));
      if (isEditMode) {
        navigation.goBack();
      } else {
        navigation.navigate('InitiativeDetail', { id: savedInitiative.id });
      }
    } catch (error: any) {
      console.error('Error saving initiative:', error);
      console.error('Error response:', error.response?.data);

      // Build detailed error message
      let errorMessage = isEditMode ? t('messages.error.saveFailed') : t('messages.error.saveFailed');
      const errorData = error.response?.data;

      if (errorData) {
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.non_field_errors?.[0]) {
          errorMessage = errorData.non_field_errors[0];
        } else {
          // Check for field-specific errors
          const fieldErrors: string[] = [];
          const fieldNames: Record<string, string> = {
            title: t('initiatives.initiativeName'),
            description: t('common.description'),
            strategic_goal: t('initiatives.linkedGoal'),
            responsible_person: t('initiatives.responsible'),
            start_date: t('initiatives.startDate'),
            end_date: t('initiatives.endDate'),
            budget: t('initiatives.budget'),
            status: t('initiatives.status'),
          };

          for (const [field, messages] of Object.entries(errorData)) {
            if (Array.isArray(messages) && messages.length > 0) {
              const fieldName = fieldNames[field] || field;
              fieldErrors.push(`${fieldName}: ${messages[0]}`);
            }
          }

          if (fieldErrors.length > 0) {
            errorMessage = fieldErrors.join(' | ');
          }
        }
      }

      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  const { requestCancel, confirmVisible, confirmDiscard, dismissConfirm } = useCancelConfirm({
    snapshot: { formData, kpis },
    ready: !initialLoading,
    onExit: handleCancel,
  });

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Render KPI Form Card
  const renderKPICard = (kpi: KPIFormData, index: number) => (
    <View key={kpi.id} style={[styles.kpiCard, { backgroundColor: themeColors.kpiCard, borderColor: themeColors.kpiCardBorder }]}>
      {/* KPI Header */}
      <View style={[styles.kpiCardHeader, { backgroundColor: themeColors.kpiCardHeader, borderBottomColor: themeColors.inputBorder }]}>
        <View style={styles.kpiCardHeaderLeft}>
          <TouchableOpacity
            style={styles.kpiHeaderButton}
            onPress={() => handleToggleKPI(kpi.id)}
          >
            {kpi.isExpanded ? (
              <ChevronUpIcon size={20} color={themeColors.iconColor} />
            ) : (
              <ChevronDownIcon size={20} color={themeColors.iconColor} />
            )}
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.kpiHeaderButton}
            onPress={() => handleRemoveKPI(kpi.id)}
          >
            <TrashIcon size={18} color={themeColors.iconColor} />
          </TouchableOpacity>
        </View>
        <View style={styles.kpiCardHeaderRight}>
          <Text style={[styles.kpiCardTitle, { color: themeColors.inputText }]}>
            {kpi.name || `${t('kpis.kpiName')} ${index + 1}`}
          </Text>
          <View style={[styles.kpiNumberBadge, { backgroundColor: themeColors.kpiNumber }]}>
            <Text style={[styles.kpiNumberText, { color: themeColors.inputText }]}>{index + 1}</Text>
          </View>
          <DragHandleIcon size={20} color={themeColors.iconColor} />
        </View>
      </View>

      {/* KPI Content (Collapsible) */}
      {kpi.isExpanded && (
        <View style={styles.kpiCardContent}>
          {/* Basic Information */}
          <View style={[styles.subsection, { backgroundColor: themeColors.section }]}>
            <Text style={[styles.subsectionTitle, { color: themeColors.sectionTitle }]}>{t('goals.basicInfo')}</Text>

            <View style={[styles.row, isMobile && styles.rowMobile]}>
              <View style={styles.fieldSmall}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('common.weight')}</Text>
                </View>
                <View style={styles.weightInputRow}>
                  <TouchableOpacity style={[styles.setWeightButton, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                    <Text style={[styles.setWeightButtonText, { color: themeColors.inputText }]}>{t('goals.setWeight')}</Text>
                  </TouchableOpacity>
                  <Text style={[styles.percentSymbol, { color: themeColors.iconColor }]}>%</Text>
                  <TextInput
                    style={[styles.weightInput, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                    value={kpi.weight}
                    onChangeText={(v) => updateKPIField(kpi.id, 'weight', normalizeArabicNumbers(v))}
                    keyboardType="numeric"
                    textAlign="right"
                  />
                </View>
              </View>

              <View style={styles.fieldLarge}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.kpiName')}</Text>
                </View>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={kpi.name}
                  onChangeText={(v) => updateKPIField(kpi.id, 'name', v)}
                  placeholder=""
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                />
              </View>
            </View>

            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={styles.requiredStar}>*</Text>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.kpiDescription')}</Text>
              </View>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={kpi.description}
                onChangeText={(v) => updateKPIField(kpi.id, 'description', v)}
                placeholder=""
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
                multiline
                numberOfLines={3}
              />
            </View>
          </View>

          {/* Classification */}
          <View style={[styles.subsection, { zIndex: 30, backgroundColor: themeColors.section }]}>
            <Text style={[styles.subsectionTitle, { color: themeColors.sectionTitle }]}>{t('kpis.classification')}</Text>

            <View style={[styles.row, { zIndex: 20 }]}>
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.polarity')}</Text>
                </View>
                <Dropdown
                  value={kpi.polarity}
                  options={getPolarityOptions(t)}
                  placeholder={t('kpis.selectPolarityHint')}
                  onChange={(v) => updateKPIField(kpi.id, 'polarity', v)}
                />
              </View>

              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.kpiClassification')}</Text>
                </View>
                <Dropdown
                  value={kpi.kpiType}
                  options={getKpiTypeOptions(t)}
                  placeholder={t('kpis.selectClassification')}
                  onChange={(v) => updateKPIField(kpi.id, 'kpiType', v)}
                />
              </View>
            </View>

            <View style={{ marginTop: tokens.spacing.absolute['3'], zIndex: 5 }}>
              <Text style={[styles.label, { color: themeColors.label, marginBottom: 8 }]}>{t('initiatives.implementationResponsibility')}</Text>
              <MultiSelectDropdown
                values={kpi.responsibleUnits}
                options={(() => {
                  const related = formData.organizationalUnits;
                  // The parent's units (and any already saved), in tree order, listed flat.
                  return orgUnitOptions
                    .filter(o => related.includes(o.value) || kpi.responsibleUnits.includes(o.value))
                    .map(({ value, label }) => ({ value, label }));
                })()}
                placeholder={t('initiatives.selectResponsibleUnits')}
                emptyText={t('common.selectResponsibilityAboveFirst')}
                onChange={(vals) => setKpis(prev => prev.map(k => k.id === kpi.id ? { ...k, responsibleUnits: vals } : k))}
              />
            </View>

          </View>

          <KpiAnnualTargetSection
            config={kpi.target}
            onChange={(patch) =>
              setKpis(prev =>
                prev.map(k => (k.id === kpi.id ? { ...k, target: { ...k.target, ...patch } } : k))
              )
            }
            unit={kpi.unit}
            frequency={kpi.frequency}
            startDate={kpi.startDate}
            formula={kpi.formula}
            dataSource={kpi.dataSource}
            onMeasurementChange={(field, value) => updateKPIField(kpi.id, field, value)}
          />
        </View>
      )}
    </View>
  );

  // Show loading while fetching initiative data in edit mode
  if (initialLoading) {
    return (
      <PageContainer>
        <View style={styles.loadingFullContainer}>
          <ActivityIndicator size="large" color={tokens.colors.semantic.general["Main Color"]} />
          <Text style={[styles.loadingText, { color: themeColors.iconColor }]}>{t('common.loadingData')}</Text>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        contentContainerStyle={[
          styles.content,
          { padding: containerPadding }
        ]}
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home'), route: 'Dashboard' },
            { label: t('navigation.initiatives'), route: 'Initiatives' },
            { label: isEditMode ? t('initiatives.editInitiative') : t('initiatives.addInitiative') },
          ]}
        />

        {/* Main Card Container */}
        <View style={[styles.mainCard, isMobile && { padding: tokens.spacing.absolute["4"] }, { backgroundColor: themeColors.mainCard, borderColor: themeColors.mainCardBorder }]}>
          {/* Page Title */}
          <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>{isEditMode ? t('initiatives.editInitiative') : t('initiatives.addInitiative')}</Text>

          {/* Basic Information Section */}
          <View style={[styles.section, { zIndex: 30, backgroundColor: themeColors.section, borderColor: themeColors.inputBorder }, isMobile && { padding: tokens.spacing.absolute["4"] }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('goals.basicInfo')}</Text>

            {/* Row 1: Initiative Title only (right side, left empty) */}
            <View style={[styles.row, isMobile && { flexDirection: 'column', gap: tokens.spacing.absolute["4"] }]}>
              {/* Empty left side - hidden on mobile */}
              {!isMobile && <View style={styles.fieldHalf} />}

              {/* Initiative Title - Right side */}
              <View style={[styles.fieldHalf, isMobile && { width: '100%' }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('initiatives.initiativeTitle')}</Text>
                </View>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }, errors.title && styles.inputError]}
                  value={formData.title}
                  onChangeText={(v) => updateField('title', v)}
                  placeholder=""
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                />
                {errors.title && <Text style={styles.errorText}>{errors.title}</Text>}
              </View>
            </View>

            {/* Row 2: Description - Full width */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={styles.requiredStar}>*</Text>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('initiatives.initiativeDescription')}</Text>
              </View>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }, errors.description && styles.inputError]}
                value={formData.description}
                onChangeText={(v) => updateField('description', v)}
                placeholder=""
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
                multiline
                numberOfLines={3}
              />
              {errors.description && <Text style={styles.errorText}>{errors.description}</Text>}
            </View>

            {/* Row 3: Strategic Goal (right) + Organizational Unit (left) */}
            <View style={[styles.row, { zIndex: 20 }, isMobile && { flexDirection: 'column', gap: tokens.spacing.absolute["4"] }]}>
              {/* Organizational Unit - Left side */}
              <View style={[styles.fieldHalf, isMobile && { width: '100%' }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label, marginBottom: 8 }]}>{t('initiatives.implementationResponsibility')}</Text>
                </View>
                {unitsLoading ? (
                  <View style={[styles.loadingContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                    <ActivityIndicator size="small" color={tokens.colors.semantic.general["Main Color"]} />
                  </View>
                ) : orgUnitOptions.length === 0 ? (
                  <View style={styles.noDataContainer}>
                    <Text style={styles.noDataText}>{t('organizations.noUnits')}</Text>
                  </View>
                ) : (
                  <MultiSelectDropdown
                    values={formData.organizationalUnits}
                    options={orgUnitOptions}
                    placeholder={t('initiatives.selectResponsibleUnits')}
                    onChange={(vals) => {
                      setFormData(prev => ({ ...prev, organizationalUnits: vals }));
                      if (errors.organizationalUnits) {
                        setErrors(prev => ({ ...prev, organizationalUnits: '' }));
                      }
                    }}
                  />
                )}
                {errors.organizationalUnits && <Text style={styles.errorText}>{errors.organizationalUnits}</Text>}
              </View>

              {/* Strategic Goal - Right side */}
              <View style={[styles.fieldHalf, isMobile && { width: '100%' }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('initiatives.strategicGoal')}</Text>
                </View>
                {goalsLoading ? (
                  <View style={[styles.loadingContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                    <ActivityIndicator size="small" color={tokens.colors.semantic.general["Main Color"]} />
                  </View>
                ) : goals.length === 0 ? (
                  <View style={styles.noDataContainer}>
                    <Text style={styles.noDataText}>{t('dashboard.noGoalsCurrently')}</Text>
                  </View>
                ) : (
                  <Dropdown
                    value={formData.strategicGoal}
                    options={goalOptions}
                    placeholder={t('risks.selectStrategicGoal')}
                    onChange={(v) => updateField('strategicGoal', v)}
                  />
                )}
                {errors.strategicGoal && <Text style={styles.errorText}>{errors.strategicGoal}</Text>}
              </View>
            </View>

            {/* Row 4: Resources - Full width */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('initiatives.resources')}</Text>
              </View>
              <TextInput
                style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={formData.resources}
                onChangeText={(v) => updateField('resources', v)}
                placeholder={t('initiatives.resourcesPlaceholder')}
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
              />
            </View>

            {/* Row 5: Planned Period */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('initiatives.plannedPeriod')}</Text>
              </View>
              <View style={[styles.row, { marginBottom: tokens.spacing.absolute["0"], gap: tokens.spacing.absolute["6"] }, isMobile && { flexDirection: 'column', gap: tokens.spacing.absolute["4"] }]}>
                {/* End Date - Left side */}
                <View style={[styles.fieldHalf, isMobile && { width: '100%' }]}>
                  <View style={styles.labelRow}>
                    <Text style={[styles.label, { color: themeColors.label }]}>{t('initiatives.toDate')}</Text>
                  </View>
                  <View style={[styles.dateInputContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                    {Platform.OS === 'web' ? (
                      <input
                        type="date"
                        value={formData.endDate}
                        onChange={(e: any) => updateField('endDate', e.target.value)}
                        style={{
                          flex: 1,
                          border: 'none',
                          outline: 'none',
                          backgroundColor: 'transparent',
                          fontSize: tokens.typography.paragraph["paragraph small font size"],
                          fontFamily: 'inherit',
                          color: isDark ? tokens.colors.raw.gray["200"] : tokens.colors.semantic.general.foreground,
                          textAlign: getTextAlign(isRTL),
                          direction: 'rtl' as const,
                          padding: '7.5px 0',
                          cursor: 'pointer',
                        }}
                      />
                    ) : (
                      <>
                        <CalendarIcon size={16} color={themeColors.iconColor} />
                        <TextInput
                          style={[styles.dateInput, { color: themeColors.inputText }]}
                          value={formData.endDate}
                          onChangeText={(v) => updateField('endDate', v)}
                          placeholder={t('initiatives.selectEndDate')}
                          placeholderTextColor={themeColors.placeholder}
                          textAlign="right"
                        />
                      </>
                    )}
                  </View>
                </View>

                {/* Start Date - Right side */}
                <View style={[styles.fieldHalf, isMobile && { width: '100%' }]}>
                  <View style={styles.labelRow}>
                    <Text style={[styles.label, { color: themeColors.label }]}>{t('initiatives.fromDate')}</Text>
                  </View>
                  <View style={[styles.dateInputContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                    {Platform.OS === 'web' ? (
                      <input
                        type="date"
                        value={formData.startDate}
                        onChange={(e: any) => updateField('startDate', e.target.value)}
                        style={{
                          flex: 1,
                          border: 'none',
                          outline: 'none',
                          backgroundColor: 'transparent',
                          fontSize: tokens.typography.paragraph["paragraph small font size"],
                          fontFamily: 'inherit',
                          color: isDark ? tokens.colors.raw.gray["200"] : tokens.colors.semantic.general.foreground,
                          textAlign: getTextAlign(isRTL),
                          direction: 'rtl' as const,
                          padding: '7.5px 0',
                          cursor: 'pointer',
                        }}
                      />
                    ) : (
                      <>
                        <CalendarIcon size={16} color={themeColors.iconColor} />
                        <TextInput
                          style={[styles.dateInput, { color: themeColors.inputText }]}
                          value={formData.startDate}
                          onChangeText={(v) => updateField('startDate', v)}
                          placeholder={t('initiatives.selectStartDate')}
                          placeholderTextColor={themeColors.placeholder}
                          textAlign="right"
                        />
                      </>
                    )}
                  </View>
                </View>
              </View>
            </View>

          </View>

          {/* KPIs Section */}
          <View
            style={[styles.kpisSection, { backgroundColor: themeColors.section }]}
            onLayout={(event) => {
              kpiSectionY.current = event.nativeEvent.layout.y;
            }}
          >
            {/* Section Header */}
            <TouchableOpacity
              style={styles.kpisSectionHeader}
              onPress={() => setKpiSectionExpanded(!kpiSectionExpanded)}
            >
              {kpiSectionExpanded ? (
                <ChevronUpIcon size={20} color={themeColors.iconColor} />
              ) : (
                <ChevronDownIcon size={20} color={themeColors.iconColor} />
              )}
              <Text style={[styles.kpisSectionTitle, { color: themeColors.sectionTitle }]}>{t('kpis.performanceIndicators')}</Text>
            </TouchableOpacity>

            {kpiSectionExpanded && (
              <View style={styles.kpisSectionContent}>
                {/* KPI Cards */}
                {kpis.map((kpi, index) => renderKPICard(kpi, index))}

                {/* Add KPI Button */}
                <TouchableOpacity style={[styles.addKpiButton, { borderColor: themeColors.inputBorder }]} onPress={handleAddKPI}>
                  <Text style={[styles.addKpiButtonText, { color: themeColors.inputText }]}>{t('kpis.addKPI')}</Text>
                  <PlusIcon size={16} color={themeColors.inputText} />
                </TouchableOpacity>
              </View>
            )}
          </View>

          {/* Action Buttons */}
          <View style={[styles.actionButtons, isMobile && { flexDirection: 'column', width: '100%' }]}>
            <Button
              variant="primary"
              onPress={handleSave}
              loading={loading}
              fullWidth={isMobile}
            >
              {t('common.save')}
            </Button>
            <Button
              variant="secondary"
              onPress={requestCancel}
              disabled={loading}
              fullWidth={isMobile}
            >
              {t('common.cancel')}
            </Button>
          </View>
        </View>
      </ScrollView>
      <KPIDeleteConfirmModal
        visible={kpiPendingDelete !== null}
        kpiId={kpiPendingDelete?.backendId ?? null}
        kpiName={kpiPendingDelete?.name}
        onConfirm={confirmRemoveKPI}
        onCancel={() => setKpiPendingDelete(null)}
      />
      <CancelConfirmModal
        visible={confirmVisible}
        onConfirm={confirmDiscard}
        onClose={dismissConfirm}
      />
    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: tokens.spacing.absolute["6"],
  },
  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    marginBottom: tokens.spacing.absolute["6"],
  },
  breadcrumbLink: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  breadcrumbActive: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general["Main Color"],
  },
  // Main Card
  mainCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["6"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    overflow: 'visible',
  },
  // Page Title
  pageTitle: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["6"],
  },
  // Section
  section: {
    backgroundColor: tokens.colors.raw.slate["50"],
    borderRadius: tokens.radii.absolute["radius-4"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["6"],
    marginBottom: tokens.spacing.absolute["4"],
    overflow: 'visible',
    gap: tokens.spacing.absolute["6"],
  },
  sectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.medium,
    fontWeight: '700',
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  // Field styles
  field: {
    marginBottom: tokens.spacing.absolute["4"],
  },
  fieldSmall: {
    width: '30%',
    minWidth: 100,
  },
  fieldLarge: {
    flex: 1,
  },
  fieldHalf: {
    flex: 1,
    overflow: 'visible',
  },
  row: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
    overflow: 'visible',
  },
  rowMobile: {
    flexDirection: 'column',
  },
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
    marginBottom: tokens.spacing.absolute["2"],
  },
  label: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  requiredStar: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: '#D62728',
  },
  // Input styles
  input: {
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 7.5,
    minHeight: 36,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    writingDirection: 'rtl',
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  textArea: {
    minHeight: 60,
    textAlignVertical: 'top',
  },
  inputError: {
    borderColor: tokens.colors.raw.red["500"],
  },
  // Percent Input
  percentInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
  },
  percentInput: {
    flex: 1,
    paddingVertical: tokens.spacing.absolute["2_5"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  percentLabel: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
  },
  // Weight Input
  weightInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  weightInput: {
    flex: 1,
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  percentSymbol: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
  },
  setWeightButton: {
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
  },
  setWeightButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  // Date Input
  dateInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    minHeight: 36,
    gap: tokens.spacing.absolute["2"],
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  dateInput: {
    flex: 1,
    paddingVertical: tokens.spacing.absolute["2_5"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  // Loading
  loadingContainer: {
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingVertical: tokens.spacing.absolute["3"],
    alignItems: 'center',
  },
  noDataContainer: {
    backgroundColor: tokens.colors.raw.amber["100"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: '#F59E0B',
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    alignItems: 'center',
  },
  noDataText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: '#92400E',
    textAlign: 'center',
  },
  loadingFullContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: tokens.spacing.absolute["4"],
  },
  loadingText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
    textAlign: 'center',
  },
  // Dropdown styles
  dropdownContainer: {
    position: 'relative',
    zIndex: 1000,
  },
  dropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 7.5,
    minHeight: 36,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  dropdownDisabled: {
    backgroundColor: tokens.colors.raw.slate["100"],
  },
  dropdownButtonText: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  dropdownPlaceholder: {
    color: tokens.colors.raw.slate["500"],
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    start: 0,
    end: 0,
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-12"],
    marginTop: tokens.spacing.absolute["1"],
    boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)',
    elevation: 20,
    zIndex: 9999,
    maxHeight: 200,
    paddingVertical: tokens.spacing.absolute["2"],
  },
  dropdownItem: {
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["100"],
  },
  dropdownItemText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  // KPIs Section
  kpisSection: {
    backgroundColor: tokens.colors.raw.slate["50"],
    borderRadius: tokens.radii.absolute["radius-8"],
    overflow: 'visible',
  },
  kpisSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: tokens.spacing.absolute["4"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["2"],
    minHeight: 44,
  },
  kpisSectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: 'center',
  },
  kpisSectionContent: {
    padding: tokens.spacing.absolute["4"],
    paddingTop: tokens.spacing.absolute["0"],
    overflow: 'visible',
  },
  // KPI Card
  kpiCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    marginBottom: tokens.spacing.absolute["3"],
    overflow: 'visible',
  },
  kpiCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["3"],
    backgroundColor: tokens.colors.raw.white,
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["200"],
  },
  kpiCardHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
  },
  kpiCardHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
  },
  kpiHeaderButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  kpiCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  kpiNumberBadge: {
    width: 28,
    height: 28,
    borderRadius: tokens.radii.absolute["radius-6"],
    backgroundColor: tokens.colors.raw.slate["100"],
    justifyContent: 'center',
    alignItems: 'center',
  },
  kpiNumberText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
  },
  kpiCardContent: {
    padding: tokens.spacing.absolute["4"],
    overflow: 'visible',
  },
  subsection: {
    backgroundColor: tokens.colors.raw.slate["50"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
    overflow: 'visible',
  },
  subsectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: 'center',
    marginBottom: tokens.spacing.absolute["4"],
  },
  // Add KPI Button
  addKpiButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    borderStyle: 'dashed',
    backgroundColor: tokens.colors.raw.white,
    minHeight: 44,
  },
  addKpiButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  // Action Buttons
  actionButtons: {
    flexDirection: 'row',
    direction: 'ltr' as any,
    justifyContent: 'flex-start',
    gap: tokens.spacing.absolute["3"],
    marginTop: tokens.spacing.absolute["2"],
  },
  // Error styles
  errorText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.red["500"],
    textAlign: getTextAlign(isRTL),
    marginTop: tokens.spacing.absolute["1"],
  },
});

const styles = getStyles(true);
